import { DataGridUpdatedRow } from '@globaleris/ui.library'
import moment from 'moment'
import {
  EnumOperando,
  EnumTipoBalance,
  EnumTipoColumna,
  EnumTipoOperacion,
  EnumZonaBalance
} from '../../api/oData/enums'
import { ApiBalancePioneer } from '../../api/Pioneer'
import { CalculoBalanceRequest } from '../../api/Pioneer/endpoints/requests/calculoBalance.request'
import { enumKeyToValue } from '../../helpers'
import i18n from '../../i18n'
import {
  mapBalanceDataGridColumns,
  mapBalanceListFromApi,
  mapBalanceOperationCalculationListFromApi,
  mapBalanceOperationCalculationListToApi,
  mapBalanceOperationCalculationToApi,
  mapGuardarBalanceRequestToApi,
  mapOperacionCalculoListFromApi
} from './balances.mapper'
import {
  BalanceColumnModel,
  BalanceModel,
  BalanceOperationCalculationModel,
  StatusBalanceResponse
} from './balances.model'

import { EnumTipoValor } from '../../api/oData/enums/enumTipoValor'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import { getActiveBalanceZone, getActiveCompany, notifyError } from '../../redux'

const apiPioneer = new ApiBalancePioneer()

export const fetchBalances = async (): Promise<ServiceResponse<any>> => {
  return await apiPioneer
    .fetch()
    .then(res => {
      return createSuccessServiceResponse(mapBalanceListFromApi(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchBalanceData = async (
  balanceId: number,
  startDate: Date,
  endDate: Date
): Promise<ServiceResponse<any>> => {
  return await apiPioneer
    .get({
      idBalance: balanceId,
      fechaInicio: new Date(moment(startDate).format('YYYY-MM-DD')),
      fechaFin: new Date(moment(endDate).format('YYYY-MM-DD')),
      idEmpresa: getActiveCompany()?.id!
    })
    .then(res => {
      return createSuccessServiceResponse(mapBalanceOperationCalculationListFromApi(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const callBalanceCalculation = async (
  request: CalculoBalanceRequest
): Promise<BalanceOperationCalculationModel[] | null> => {
  return await apiPioneer
    .getCalculation(request)
    .then(res => {
      return mapOperacionCalculoListFromApi(res)
    })
    .catch(() => {
      notifyError(i18n.t('balance:notifications.error.getBalanceCalculation'))
      return null
    })
}

export const saveBalance = async (balance: BalanceModel) => {
  const manuallyModifiedOperations = balance.operations?.filter(x => x.modified === true)

  if (manuallyModifiedOperations !== undefined && manuallyModifiedOperations.length > 0) {
    await apiPioneer.save(mapGuardarBalanceRequestToApi(manuallyModifiedOperations)).catch(() => {
      notifyError(i18n.t('balance:notifications.error.saveBalance'))
      return null
    })
  }
}

export const enhanceBalanceDataWithColumnHighlighting = (
  balanceData: any[],
  operations: BalanceOperationCalculationModel[],
  columnsConfiguration: BalanceColumnModel[]
): any[] => {
  // Highlighting is now calculated during balance calculation (in calculateBalance function)
  return balanceData
}

export const enhanceBalanceDataWithHighlighting = (
  balanceData: any[],
  operations: BalanceOperationCalculationModel[],
  columnsConfiguration: BalanceColumnModel[]
): any[] => {
  return balanceData.map((row, rowIndex) => {
    const rowDate = moment(row.date, 'DD/MM/YYYY').format('YYYY-MM-DD')

    // Check if any operation for this row/date has areQuantitiesDifferent: true
    // AND the column allows highlighting (including related columns for aggregated columns)
    const hasQuantityDifferences = operations.some(operation => {
      const operationDate = moment(operation.date).format('YYYY-MM-DD')

      if (operationDate !== rowDate || !operation.areQuantitiesDifferent) {
        return false
      }

      // Check if this column allows highlighting
      const columnConfig = columnsConfiguration.find(col => col.id === operation.balanceColumnId)
      const allowHighlighting = columnConfig?.allowHighlighting !== false // Default to true if not specified

      if (!allowHighlighting) {
        return false
      }

      const isPartOfAggregatedColumn = columnsConfiguration.some(col => {
        return (
          col.type === EnumTipoColumna.Agregado &&
          col.allowHighlighting !== false &&
          col.relatedColumns.some(rc => rc.relatedColumnId === operation.balanceColumnId)
        )
      })

      return allowHighlighting || isPartOfAggregatedColumn
    })

    return {
      ...row,
      isHighlighted: hasQuantityDifferences
    }
  })
}

const defaultDateFormat = 'DD/MM/YYYY'
const dateColumnId = 'date'

export const recalculateBalance = (
  balance: BalanceModel | null,
  startDate: Date,
  endDate: Date
): BalanceModel | null => {
  if (balance === null) return null
  const periodDays = moment(moment(endDate).format(defaultDateFormat), defaultDateFormat).diff(
    moment(moment(startDate).format(defaultDateFormat), defaultDateFormat),
    'days'
  )
  // eslint-disable-next-line no-loop-func
  let previousDayData: any = createVirtualBalanceRow(startDate, balance)

  for (let i = 0; i < periodDays + 1; i++) {
    let row = {}
    const currentDate = moment(startDate).add(i, 'days').format(defaultDateFormat)
    row[dateColumnId] = currentDate

    const dayOperations = balance.operations?.filter(
      x => moment(x.date).format(defaultDateFormat) === currentDate
    )
    
    // eslint-disable-next-line no-loop-func
    balance.columnsConfiguration.map(column => {
      row[column.id.toString()] = calculateBalanceColumn(
        currentDate,
        balance,
        column,
        dayOperations,
        previousDayData
      )

      if (column.operationType === EnumTipoOperacion.Existencias) {
        dayOperations && replaceStockQuantity(currentDate, dayOperations, row[column.id.toString()])
      }

      return row
    })

    previousDayData = row
    const dayDataIndex = balance.data?.findIndex(x => x.date === currentDate)

    if (balance.data !== undefined && dayDataIndex !== undefined) {
      balance.data[dayDataIndex] = row
    }
  }

  return structuredClone(balance)
}

export const calculateBalance = (
  balance: BalanceModel,
  operations: Array<BalanceOperationCalculationModel>,
  startDate: Date,
  endDate: Date
): BalanceModel => {
  balance.columnsConfiguration = balance.columnsConfiguration.sort((a, b) =>
    a.order > b.order ? 1 : -1
  )

  balance.columns = mapBalanceDataGridColumns(balance.columnsConfiguration)
  balance.operations = operations
  balance.data = []

  const periodDays = moment(moment(endDate).format(defaultDateFormat), defaultDateFormat).diff(
    moment(moment(startDate).format(defaultDateFormat), defaultDateFormat),
    'days'
  )

  let previousDayData: any = createVirtualBalanceRow(startDate, balance)
  for (let i = 0; i < periodDays + 1; i++) {
    let row = {}
    const currentDate = moment(startDate).add(i, 'days').format(defaultDateFormat)
    row[dateColumnId] = currentDate

    const dayOperations = balance.operations?.filter(
      x => moment(x.date).format(defaultDateFormat) === currentDate
    )

    // eslint-disable-next-line no-loop-func
    balance.columnsConfiguration.map(column => {
      const operationIsExistences =
        column.operationType === EnumTipoOperacion.Existencias &&
        moment(currentDate, defaultDateFormat).isBefore(moment(), 'day')

      const applyAggregatePolicy = !operationIsExistences

      row[column.id.toString()] = calculateBalanceColumn(
        currentDate,
        balance,
        column,
        dayOperations,
        previousDayData,
        applyAggregatePolicy
      )

      // Calculate and store highlighting information for this cell
      const allowHighlighting = column.allowHighlighting !== false
      if (allowHighlighting) {
        const hasColumnDifference = checkColumnHasDifferences(
          currentDate,
          column,
          dayOperations,
          balance.columnsConfiguration
        )
        row[`${column.id}Highlighted`] = hasColumnDifference

        // Store cell icon info (TSO difference) for non-aggregated columns
        if (hasColumnDifference && column.type !== EnumTipoColumna.Agregado) {
          const operation = dayOperations?.find(
            x => x.balanceColumnId === column.id && x.areQuantitiesDifferent === true
          )
          if (
            operation &&
            operation.quantityTSO !== undefined &&
            operation.quantity !== undefined
          ) {
            row[`${column.id}hasInfo`] = true
            row[`${column.id}tsoQuantity`] = operation.quantityTSO
            row['hasInfo'] = true // Global flag required for library to check for icons on this row
          }
        }
      }

      if (column.operationType === EnumTipoOperacion.Existencias) {
        replaceStockQuantity(currentDate, dayOperations, row[column.id.toString()])
      }
      return row
    })

    previousDayData = row
    balance?.data?.push(row)
  }
  return balance
}

/// Replace API stock operation quantity with calculate one on a specific date
export const replaceStockQuantity = (
  date: string,
  operations: BalanceOperationCalculationModel[],
  quantity: number
) => {
  const op = operations.find(
    x =>
      moment(x.date).format(defaultDateFormat) === date && x.type === EnumTipoOperacion.Existencias
  )
  if (op !== undefined) {
    op.quantity = quantity
  }
}

export const createVirtualBalanceRow = (date: Date, balance: BalanceModel): any => {
  let row = {}
  row['date'] = moment(date).add(-1, 'days').format(defaultDateFormat)

  const periodPreviousDayData = balance.operations?.filter(
    x =>
      moment(x.date).format(defaultDateFormat) ===
      moment(date).add(-1, 'days').format(defaultDateFormat)
  )

  balance.columnsConfiguration.map(column => {
    row[column.id.toString()] = calculateBalanceColumn(
      moment(date).add(-1, 'days').format(defaultDateFormat),
      balance,
      column,

      periodPreviousDayData,
      undefined,
      false
    )

    return row
  })

  return row
}

export const checkColumnHasDifferences = (
  date: string,
  column: BalanceColumnModel,
  operations: Array<BalanceOperationCalculationModel> | undefined,
  columnsConfiguration: BalanceColumnModel[]
): boolean => {
  if (!operations) return false

  // Check if it's an aggregated column
  if (column.type === EnumTipoColumna.Agregado) {
    // For aggregated columns, check if any of the related columns have differences
    return column.relatedColumns.some(relatedColumn => {
      return operations.some(operation => {
        const operationDate = moment(operation.date).format(defaultDateFormat)
        return (
          operationDate === date &&
          operation.balanceColumnId === relatedColumn.relatedColumnId &&
          operation.areQuantitiesDifferent === true
        )
      })
    })
  } else {
    // For regular operation columns, check the column itself
    return operations.some(operation => {
      const operationDate = moment(operation.date).format(defaultDateFormat)
      return (
        operationDate === date &&
        operation.balanceColumnId === column.id &&
        operation.areQuantitiesDifferent === true
      )
    })
  }
}

export const calculateBalanceColumn = (
  date: string,
  balance: BalanceModel,
  column: BalanceColumnModel,
  operations: Array<BalanceOperationCalculationModel> | undefined,
  previousDayData: any,
  applyAggregatePolicy: boolean = true
): number => {
  if (operations === undefined) return 0
  switch (column.type) {
    case EnumTipoColumna.Agregado: {
      return applyAggregatePolicy
        ? calculateAggregateBalanceColumn(date, balance, column, operations, previousDayData)
        : calculateDefaultBalanceColumn(date, column, operations, balance.zone)
    }
    default: {
      return calculateDefaultBalanceColumn(date, column, operations, balance.zone)
    }
  }
}

export const calculateDefaultBalanceColumn = (
  date: string,
  column: BalanceColumnModel,
  data: Array<BalanceOperationCalculationModel>,
  balanceZone: EnumZonaBalance
): any => {
  const x = data.find(
    x =>
      moment(x.date).format(defaultDateFormat) === date &&
      x.type === column.operationType &&
      x.logisticElementId === column.logisticElementId &&
      enumKeyToValue(EnumZonaBalance, x.zone) === balanceZone
  )

  let qty = x?.modified ? `${x.quantity}*` : x?.quantity

  if (
    x?.typeValue !== null &&
    x?.typeValue !== undefined &&
    x?.value !== null &&
    x?.value !== undefined
  ) {
    qty = x?.modified ? `${x?.value}*` : x?.value
  }

  return qty !== undefined && qty !== null ? qty : 0
}

export const calculateAggregateBalanceColumn = (
  date: string,
  balance: BalanceModel,
  column: BalanceColumnModel,
  data: Array<BalanceOperationCalculationModel>,
  previousDayData: any
): number => {
  let totalQty = 0

  column.relatedColumns.forEach(relatedColumn => {
    const balanceColumnConfiguration = balance?.columnsConfiguration.find(
      x => x.id === relatedColumn.relatedColumnId
    )

    const x = data.find(
      x =>
        moment(x.date).format(defaultDateFormat) === date &&
        x.balanceColumnId === relatedColumn.relatedColumnId &&
        x.logisticElementId === balanceColumnConfiguration?.logisticElementId &&
        enumKeyToValue(EnumZonaBalance, x.zone) === balance.zone
    )

    let qty = x?.quantity

    if (
      x?.typeValue !== null &&
      x?.typeValue !== undefined &&
      (x?.typeValue === EnumTipoValor.Decimal || x?.typeValue === EnumTipoValor.Int) &&
      x?.value !== null &&
      x?.value !== undefined
    ) {
      qty = +x?.value
    }

    if (relatedColumn.type === EnumOperando.SUMA_BL) {
      if (qty != null) {
        totalQty += qty
      }
    } else if (relatedColumn.type === EnumOperando.RESTA_BL) {
      if (qty != null) {
        totalQty -= qty
      }
    }
  })

  if (column.relatedColumns.findIndex(x => x.relatedColumnId === column.id) !== -1) {
    totalQty += previousDayData[column.id]
  }

  return totalQty
}

export const getDataGridColumnParent = (
  columns: Array<BalanceColumnModel>,
  id: number
): Array<string> | undefined => {
  const subcolumns = columns.filter(
    column => column.parentColumnId === id && column.visible === true
  )

  return subcolumns && subcolumns.length > 0
    ? subcolumns.map(subcolumn => subcolumn.id.toString())
    : undefined
}

export const calculateBalanceRows = async (
  balance: BalanceModel | null,
  rows: DataGridUpdatedRow[]
): Promise<BalanceOperationCalculationModel[] | undefined> => {
  if (balance !== null && balance.data !== undefined) {
    const request: CalculoBalanceRequest = composeBalanceCalculationRequest(
      structuredClone(balance),
      rows
    )

    return await callBalanceCalculation(request).then(response => {
      updateBalanceModifiedOperations(balance, rows)

      response &&
        response.forEach(operation => {
          const existingOperation = balance?.operations?.find(
            x =>
              //x.date === operation.date &&
              moment(x.date).format(defaultDateFormat) ===
                moment(operation.date).format(defaultDateFormat) &&
              x.logisticElementId === operation.logisticElementId &&
              x.type === operation.type
          )

          if (existingOperation === undefined) {
            const relatedBalanceColumn = balance?.columnsConfiguration.find(
              x =>
                x.logisticElementId === operation.logisticElementId &&
                x.operationType === operation.type
            )

            if (relatedBalanceColumn !== undefined) {
              const newOperation: BalanceOperationCalculationModel = {
                balanceColumnId: relatedBalanceColumn?.id,
                date: operation.date,
                send: relatedBalanceColumn?.send,
                quantity: operation.quantity,
                type: operation.type,
                modified: false,
                logisticElementId: operation.logisticElementId,
                zone: operation.zone,
                companyId: operation.companyId
              }

              balance?.operations?.push(newOperation)
            }
          } else {
            existingOperation.quantity = operation.quantity
          }
        })

      return balance?.operations
    })
  }
}

const updateBalanceModifiedOperations = (
  balance: BalanceModel | null,
  rows: DataGridUpdatedRow[]
) => {
  rows.forEach(row => {
    const currentDate = balance?.data![row.row].date

    const balanceColumnConfiguration = balance?.columnsConfiguration.find(x => x.id === +row.column)

    let operationModified = balance?.operations?.find(
      x =>
        moment(x.date).format('DD/MM/YYYY') === currentDate &&
        x.balanceColumnId === +row.column &&
        x.logisticElementId === balanceColumnConfiguration?.logisticElementId
    )

    if (operationModified === undefined) {
      const relatedBalanceColumn = balance?.columnsConfiguration.find(x => x.id === +row.column)

      operationModified = createOperation(
        +row.column,
        +row.value,
        moment(currentDate, 'DD/MM/YYYY').toDate(),
        relatedBalanceColumn
      )

      balance?.operations?.push(operationModified)
    }

    operationModified.modified = true
    operationModified.quantity = +row.value
  })
}

const composeBalanceCalculationRequest = (
  balance: BalanceModel | null,
  rows: DataGridUpdatedRow[]
): CalculoBalanceRequest => {
  const request: CalculoBalanceRequest = {
    zonaBalance: enumKeyToValue(EnumZonaBalance, getActiveBalanceZone()?.id),
    calcularExistenciasAFuturo:
      enumKeyToValue(EnumZonaBalance, balance?.zone) == EnumZonaBalance.PT &&
      enumKeyToValue(EnumTipoBalance, balance?.tipo) == EnumTipoBalance.PVB
        ? true
        : false,
    opsOriginal: [],
    opsModified: []
  }

  rows.forEach(row => {
    const currentDate = balance?.data![row.row].date

    let operationModified = balance?.operations?.find(
      x => moment(x.date).format('DD/MM/YYYY') === currentDate && x.balanceColumnId === +row.column
    )

    if (operationModified === undefined) {
      const relatedBalanceColumn = balance?.columnsConfiguration.find(x => x.id === +row.column)

      operationModified = createOperation(
        +row.column,
        +row.value,
        moment(currentDate, 'DD/MM/YYYY').toDate(),
        relatedBalanceColumn
      )

      balance?.operations?.push(operationModified)
    }

    operationModified.modified = true
    operationModified.quantity = +row.value

    //#60157 - Si no es el PVB de Portugal vamos a seguir haciendo lo que se hacía antes, enviar al cálculo únicamente las operaciones del día y la existencia del día anterior
    //En el caso del PVB de portugal necesitamos poder calcular el linepack a futuro o pasado, en caso de no existir, así que enviamos todas las operaciones que se visualizan por pantalla
    if (
      !(
        enumKeyToValue(EnumZonaBalance, balance?.zone) == EnumZonaBalance.PT &&
        enumKeyToValue(EnumTipoBalance, balance?.tipo) == EnumTipoBalance.PVB
      )
    ) {
      const previousDateStockOperations = balance?.operations?.find(
        x =>
          moment(x.date).format('DD/MM/YYYY') ===
            moment(currentDate, defaultDateFormat).add(-1, 'days').format(defaultDateFormat) &&
          x.type === EnumTipoOperacion.Existencias
      )

      const dateOperations = balance?.operations?.filter(
        x => moment(x.date).format('DD/MM/YYYY') === currentDate
      )

      if (dateOperations !== undefined) {
        const dateStockOperation = dateOperations?.find(
          x => x.type === EnumTipoOperacion.Existencias
        )

        if (dateStockOperation !== undefined && previousDateStockOperations !== undefined) {
          dateStockOperation.quantity = previousDateStockOperations.quantity
          dateStockOperation.date = previousDateStockOperations.date
        }

        const existingOperation = dateOperations.find(
          x =>
            moment(x.date).format('DD/MM/YYYY') === currentDate &&
            x.type === operationModified?.type &&
            x.logisticElementId === operationModified?.logisticElementId
        )

        if (existingOperation === undefined) {
          dateOperations.push(operationModified)
        } else {
          existingOperation.quantity = operationModified.quantity
          operationModified.typeValue = existingOperation.typeValue
          operationModified.value = existingOperation.value
        }

        request.opsOriginal = request.opsOriginal.concat(
          mapBalanceOperationCalculationListToApi(dateOperations)
        )
      }

      request.opsModified.push(mapBalanceOperationCalculationToApi(operationModified))
    }
  })

  //#60157 - Para el PVB de Portugal  necesitamos poder calcular el linepack a futuro o pasado, en caso de no existir,
  // así que enviamos todas las operaciones que se visualizan por pantalla
  if (
    enumKeyToValue(EnumZonaBalance, balance?.zone) == EnumZonaBalance.PT &&
    enumKeyToValue(EnumTipoBalance, balance?.tipo) == EnumTipoBalance.PVB
  ) {
    var modifiedOps = balance?.operations?.filter(x => x.modified === true)
    if (modifiedOps) request.opsModified = mapBalanceOperationCalculationListToApi(modifiedOps)

    var originalOps = balance?.operations
    if (originalOps) request.opsOriginal = mapBalanceOperationCalculationListToApi(originalOps)
  }

  return request
}

export const createOperation = (
  columnId: number,
  quantity: number,
  date: Date,
  columnConfiguration: BalanceColumnModel | undefined
): BalanceOperationCalculationModel => {
  return {
    balanceColumnId: columnId,
    send: columnConfiguration?.send,
    date: date,
    type: columnConfiguration?.operationType,
    logisticElementId: columnConfiguration?.logisticElementId,
    quantity: quantity,
    zone: columnConfiguration?.zone,
    companyId: getActiveCompany()?.id,
    modified: false,
    typeValue: null,
    value: null
  }
}

export const resetOperationsModifiedFlag = (balance: BalanceModel): BalanceModel => {
  balance &&
    balance.operations?.forEach(operation => {
      if (operation.modified === true) operation.modified = false
    })

  balance &&
    balance.data?.forEach(row => {
      balance.columnsConfiguration.forEach(column => {
        if (row[column.id].toString().includes('*')) {
          row[column.id] = +row[column.id].replace('*', '')
        }
      })
    })

  return balance
}

export const getStatusBalance = async (): Promise<StatusBalanceResponse | null> => {
  return await apiPioneer
    .getStatusBalance()
    .then(res => {
      return res
    })
    .catch(() => {
      notifyError(i18n.t('balance:notifications.error.getBalanceCalculation'))
      return null
    })
}
